import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { VISIBILITY } from "@/components/WallComposer";

/** Owner-only: edit caption and privacy of an existing wall post. */
export default function PostOwnerEditDialog({ open, onOpenChange, post, onSaved }) {
  const [text, setText] = useState(post.text || "");
  const [visibility, setVisibility] = useState(post.visibility || "public");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setText(post.text || ""); setVisibility(post.visibility || "public"); } }, [open, post.text, post.visibility]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/posts/${post.id}`, { text, visibility });
      toast.success("Post updated."); onSaved?.(data); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="post-edit-dialog" className="bg-[#0c0c0e] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl">Edit post</DialogTitle><DialogDescription className="text-xs text-zinc-500">Change the caption or who can see this post.</DialogDescription></DialogHeader>
        <Textarea data-testid="post-edit-caption" value={text} onChange={(e) => setText(e.target.value.slice(0, 1000))} rows={4} className="bg-white/5 border-white/10 resize-none" />
        <div className="flex gap-1.5" role="radiogroup" aria-label="Who can see this">
          {VISIBILITY.map(({ key, label, Icon }) => (
            <button key={key} type="button" role="radio" aria-checked={visibility === key} data-testid={`post-edit-visibility-${key}`} onClick={() => setVisibility(key)} className={`flex-1 h-9 rounded-lg border text-xs inline-flex items-center justify-center gap-1.5 ${visibility === key ? "border-emerald-400 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-zinc-400"}`}><Icon size={12} /> {label}</button>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="post-edit-save" className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold min-w-[100px]">{busy ? <Loader2 size={16} className="animate-spin" /> : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
